/* eslint-disable react-hooks/exhaustive-deps */
import { useAuth0 } from "@auth0/auth0-react";
import { Alert, Button, Table } from "antd";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { Link } from "react-router-dom";
import { ReactSVG } from "react-svg";
import InvoiceModal from "../../../modals/InvoiceModal";
import Refund from "../../../shared/Refund";
import { getBillingData } from "../../../utils/billing";
import { formatNumber } from "../../../utils/helperFunctions";
import "./billing.scss";

const Billing = () => {
  // Auth and navigation hooks
  const { getIdTokenClaims } = useAuth0();
  const { enqueueSnackbar } = useSnackbar();

  // Component state
  const [openedIndex, setOpenedIndex] = useState(0);
  const [invoices, setInvoices] = useState([]);
  const [selectedInvoice, setSelectedInvoice] = useState({});
  const [tableLoading, setTableLoading] = useState(true);
  const [openInvoicePopup, setOpenInvoicePopup] = useState(false);

  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalRecords, setTotalRecords] = useState(0);

  // Get account data status from Redux
  const isAccountDataFilled = useSelector(
    (state) => state.commonReducer.isAccountDataFilled
  );

  // Check authentication status
  useEffect(() => {
    fetchBillingInvoices();
  }, [getIdTokenClaims, currentPage, pageSize]);

  const fetchBillingInvoices = async () => {
    setTableLoading(true);
    try {
      const token = await getIdTokenClaims();
      const response = await getBillingData(token.__raw, {
        page: currentPage,
        pageSize: pageSize,
      });

      if (response.data.success) {
        setInvoices(response.data.data);
        setTotalRecords(response.data.data.length);
      } else {
        enqueueSnackbar("Something went wrong. Please contact support", {
          variant: "error",
        });
      }
    } catch (error) {
      console.error("Error fetching billing data:", error);
    } finally {
      setTableLoading(false);
    }
  };

  const handleOpenInvoice = (invoice) => {
    setSelectedInvoice(invoice);
    setOpenInvoicePopup(true);
  };

  const formatCurrency = (amount) => {
    return `$${formatNumber(amount / 100)}`;
  };

  // Handle pagination change
  const handleTableChange = (pagination) => {
    setCurrentPage(pagination.current);
    setPageSize(pagination.pageSize);
  };

  // If account data isn't filled, show warning alert
  if (!isAccountDataFilled) {
    return (
      <Alert
        message="Missing Account Information"
        description={
          <div>
            <p>
              Please complete your account information in the Profile section
              before proceeding with billing.
            </p>
            <p>
              Full name, company name, company phone, tax number, and company
              address are required (same as My Profile).
            </p>
            <Link
              to="/settings/profile"
              className="text-blue-500 hover:text-blue-700 font-medium"
            >
              Go to Profile Settings
            </Link>
          </div>
        }
        type="warning"
        showIcon
        className="mb-4"
      />
    );
  }

  // Table columns configuration
  const columns = [
    {
      title: "Organization Name",
      dataIndex: "org_name",
      key: "name",
      width: "30%",
    },
    {
      title: "Billing Date",
      dataIndex: "payment_time",
      key: "date",
      align: "right",
      render: (date) => formatDate(date),
    },
    {
      title: "Amount",
      dataIndex: "amount_paid",
      key: "amount",
      align: "right",
      render: (_, { amount_paid }) => formatCurrency(amount_paid),
    },
    {
      title: "Action",
      dataIndex: "action",
      key: "action",
      align: "right",
      render: (_, invoice) => (
        <div className="flex justify-end gap-1">
          <Refund invoice={invoice} />

          <Button
            type="default"
            onClick={() => handleOpenInvoice(invoice)}
            className="flex items-center gap-1 rounded-xl border border-gray-300"
            icon={<ReactSVG src="/assets/icons/invoice.svg" />}
          >
            Invoice
          </Button>
        </div>
      ),
    },
  ];

  const formatDate = (dateString) => {
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString("en-US", {
        year: "numeric",
        month: "short",
        day: "numeric",
      });
    } catch (e) {
      return dateString;
    }
  };

  // Get paginated data for mobile view
  const getPaginatedMobileData = () => {
    return invoices;
  };

  // Mobile view rendering for invoices
  const renderMobileView = () => {
    const paginatedData = getPaginatedMobileData();

    if (paginatedData.length === 0 && !tableLoading) {
      return <p className="text-gray-500 mt-4">No billing history found.</p>;
    }

    return (
      <>
        {paginatedData.map((item, index) => (
          <div
            key={index}
            className="border rounded-lg border-gray-300 text-sm py-3 mt-3 px-4 text-gray-500"
          >
            <div
              className="flex items-center justify-between cursor-pointer"
              onClick={() =>
                setOpenedIndex(index === openedIndex ? null : index)
              }
            >
              <div className="flex items-center gap-2">
                <div
                  className={`text-gray-700 w-6 h-6 flex items-center justify-center bg-gray-100 rounded-full transition-transform ${openedIndex === index ? "rotate-180" : ""
                    }`}
                >
                  <ReactSVG src="/assets/icons/common-dropdown.svg" />
                </div>
                <div className="text-gray-500 font-medium">{item.org_name}</div>
              </div>
              <div className="text-gray-500 flex gap-2 items-center">
                <div className="h-2 w-2 rounded-full bg-red-300" />
                {formatCurrency(item.amount_paid)}
              </div>
            </div>

            {openedIndex === index && (
              <div className="mt-2 pt-2 border-t border-gray-200">
                <div className="flex justify-between mb-1">
                  <div>Plan type:</div>
                  <div>{item.type || "Standard"}</div>
                </div>
                <div className="flex justify-between mb-1">
                  <div>Date:</div>
                  <div>{formatDate(item.payment_time)}</div>
                </div>
                <div className="gap-1">
                  <Refund invoice={item} />
                  <Button
                    type="default"
                    size="small"
                    onClick={(e) => {
                      e.stopPropagation();
                      handleOpenInvoice(item);
                    }}
                    className="mt-2 flex items-center gap-1 text-xs"
                    icon={<ReactSVG src="/assets/icons/invoice.svg" />}
                  >
                    Invoice
                  </Button>
                </div>
              </div>
            )}
          </div>
        ))}

        {/* Mobile pagination controls */}
        {totalRecords > pageSize && (
          <div className="flex justify-center mt-4">
            <div className="pagination-controls flex items-center gap-2">
              <Button
                disabled={currentPage === 1 || tableLoading}
                onClick={() => setCurrentPage((prev) => Math.max(1, prev - 1))}
              >
                Previous
              </Button>
              <span className="text-sm">
                Page {currentPage} of{" "}
                {Math.max(1, Math.ceil(totalRecords / pageSize))}
              </span>
              <Button
                disabled={
                  currentPage >= Math.ceil(totalRecords / pageSize) ||
                  tableLoading
                }
                onClick={() => setCurrentPage((prev) => prev + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </>
    );
  };

  return (
    <div className="billing-container">
      {/* Invoice Modal */}
      {openInvoicePopup && (
        <div className="fixed z-50 flex items-center justify-center top-0 left-0 w-full h-full bg-gray-400 bg-opacity-50">
          <div
            className="w-full absolute z-10 h-full"
            onClick={() => setOpenInvoicePopup(false)}
          />
          <InvoiceModal
            invoice={selectedInvoice}
            onClose={() => setOpenInvoicePopup(false)}
          />
        </div>
      )}

      {/* Refresh button */}
      <div className="flex justify-end mb-4">
        <Button
          onClick={fetchBillingInvoices}
          icon={<ReactSVG src="/assets/icons/refresh.svg" />}
          loading={tableLoading}
        >
          Refresh
        </Button>
      </div>

      {/* Desktop view */}
      <div className="billing-table mt-6 lg:block hidden">
        <Table
          dataSource={invoices}
          columns={columns}
          loading={tableLoading}
          rowKey="id"
          locale={{ emptyText: "No billing history found" }}
          pagination={false}
          // pagination={{
          //   current: currentPage,
          //   pageSize: pageSize,
          //   total: totalRecords,
          //   showSizeChanger: true,
          //   pageSizeOptions: ["10", "20", "50"],
          //   responsive: true,
          //   showTotal: (total, range) =>
          //     `${range[0]}-${range[1]} of ${total} items`,
          //   onChange: (page, pageSize) => {
          //     setCurrentPage(page);
          //     setPageSize(pageSize);
          //   },
          // }}
          onChange={handleTableChange}
        />
      </div>

      {/* Mobile view */}
      <div className="lg:hidden block">
        {tableLoading ? (
          <div className="flex justify-center py-8">
            <div className="loading-spinner"></div>
          </div>
        ) : (
          renderMobileView()
        )}
      </div>
    </div>
  );
};

export default Billing;
